import { apiClient } from './client'
import type { ApiResponse } from './client'
import type {
    FormFieldCatalogResponseDto,
    LandingPageViewDto,
    LandingPageGenerateRequestDto,
    LandingPageSettingsUpdateDto,
    LandingPageContextDto,
    LandingPageContextUpdateDto,
    LandingPageDesignAnswersDto,
    LandingPageDeploymentDto,
    LandingPageModePatchDto,
    ThankYouPageSettingsUpdateDto,
    PageMetaUpdateDto,
    PageMetaUploadResultDto,
    ContentImageUploadResultDto,
    FontCatalogEntryDto,
    FontUploadResultDto,
    ApplyFontRequestDto,
} from '@/types/landingPage'

export const landingPagesApi = {
    createLandingPage: (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        webId: string,
        request: LandingPageGenerateRequestDto,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/LandingPage`,
            request,
        ),

    getLandingPage: (
        workspaceId: string,
        webId: string,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.get(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage`),

    getLandingPageDraft: (
        workspaceId: string,
        webId: string,
    ): Promise<ApiResponse<string>> =>
        apiClient.get(
            `/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Draft`,
        ),

    updateSettings: (
        workspaceId: string,
        webId: string,
        dto: LandingPageSettingsUpdateDto,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.request(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Settings`, {
            method: 'PATCH',
            body: dto,
        }),

    generateLandingPage: (
        workspaceId: string,
        webId: string,
        prompt?: string,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.post(
            `/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Generate`,
            prompt ? { Prompt: prompt } : {},
        ),

    getCampaignLandingPages: (
        workspaceId: string,
        campaignId: string,
    ): Promise<ApiResponse<LandingPageViewDto[]>> =>
        apiClient.get(
            `/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/LandingPages`,
        ),

    getContext: (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        webId: string,
    ): Promise<ApiResponse<LandingPageContextDto>> =>
        apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/LandingPage/Context`,
        ),

    updateContext: (
        workspaceId: string,
        webId: string,
        dto: LandingPageContextUpdateDto,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.request(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Context`, {
            method: 'PATCH',
            body: dto,
        }),

    /**
     * Answers the design questions Webster asked before building, and restarts
     * generation. Fails if the page is no longer awaiting answers — someone else got
     * there first, or it already moved on.
     */
    submitDesignAnswers: (
        workspaceId: string,
        webId: string,
        dto: LandingPageDesignAnswersDto,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.request(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/DesignAnswers`, {
            method: 'POST',
            body: dto,
        }),

    deleteLandingPage: (
        workspaceId: string,
        webId: string,
    ): Promise<ApiResponse<void>> =>
        apiClient.request(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage`, {
            method: 'DELETE',
        }),

    getThankYouPageDraft: (
        workspaceId: string,
        webId: string,
    ): Promise<ApiResponse<string>> =>
        apiClient.get(
            `/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/ThankYou/Draft`,
        ),

    updateThankYouSettings: (
        workspaceId: string,
        webId: string,
        dto: ThankYouPageSettingsUpdateDto,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.request(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/ThankYouSettings`, {
            method: 'PUT',
            body: dto,
        }),

    updatePageMeta: (
        workspaceId: string,
        webId: string,
        dto: PageMetaUpdateDto,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.request(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/PageMeta`, {
            method: 'PATCH',
            body: dto,
        }),

    regenerateThankYou: (
        workspaceId: string,
        webId: string,
        prompt?: string,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.post(
            `/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/ThankYou/Generate`,
            prompt ? { Prompt: prompt } : {},
        ),

    // Deployment lifecycle endpoints (ORB-162, ORB-163, ORB-164)
    deploy: (
        workspaceId: string,
        webId: string,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.post(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Deploy`, {}),

    unpublish: (
        workspaceId: string,
        webId: string,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.post(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Unpublish`, {}),

    rollback: (
        workspaceId: string,
        webId: string,
        targetVersionNumber: number,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.post(
            `/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Rollback`,
            { TargetVersionNumber: targetVersionNumber },
        ),

    /** ORB-241: flip Builder ↔ External. When the LP doesn't exist yet, the server creates one in External mode (Builder requires existing LP). */
    setMode: (
        workspaceId: string,
        campaignId: string,
        webId: string,
        dto: LandingPageModePatchDto,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.request(
            `/api/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/LandingPage/Mode`,
            { method: 'PATCH', body: dto },
        ),

    listDeployments: (
        workspaceId: string,
        webId: string,
    ): Promise<ApiResponse<LandingPageDeploymentDto[]>> =>
        apiClient.get(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Deployments`),

    getFormFieldCatalog: (): Promise<ApiResponse<FormFieldCatalogResponseDto>> =>
        apiClient.get('/api/LandingPages/FormFieldCatalog'),

    uploadMetaAsset: (
        workspaceId: string,
        webId: string,
        assetType: 'OgImage' | 'Favicon',
        file: File,
    ): Promise<ApiResponse<PageMetaUploadResultDto>> => {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post(
            `/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/MetaAssets/${assetType}`,
            formData,
        )
    },

    // ORB-413: upload a user-supplied content image. Works pre- and post-generation:
    // pre-gen the server stages the blob and returns the image record (the dashboard
    // carries it into the Generate body); post-gen the server also updates the LP and
    // returns the refreshed view. Single multipart field named `file`.
    uploadContentImage: (
        workspaceId: string,
        webId: string,
        file: File,
    ): Promise<ApiResponse<ContentImageUploadResultDto>> => {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post(
            `/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/ContentImages`,
            formData,
        )
    },

    /** Curated font catalog for the picker; optional case-insensitive family filter. */
    getFontCatalog: (search?: string): Promise<ApiResponse<FontCatalogEntryDto[]>> =>
        apiClient.get(
            `/api/LandingPages/FontCatalog${search ? `?search=${encodeURIComponent(search)}` : ''}`,
        ),

    /** Applies a font choice to the page instantly (no regeneration); returns the refreshed view. */
    applyFont: (
        workspaceId: string,
        webId: string,
        dto: ApplyFontRequestDto,
    ): Promise<ApiResponse<LandingPageViewDto>> =>
        apiClient.post(`/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Font`, dto),

    /** Uploads a custom .woff2 font for the page and registers it; family defaults to the file name. */
    uploadFont: (
        workspaceId: string,
        webId: string,
        family: string,
        file: File,
    ): Promise<ApiResponse<FontUploadResultDto>> => {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post(
            `/api/Workspaces/${workspaceId}/Webs/${webId}/LandingPage/Fonts/Upload?family=${encodeURIComponent(family)}`,
            formData,
        )
    },
}
